import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login, logout } from "../../features/userSlice";
import { useContextStateManagement } from "../../context/ContextApi";
import "./Auth.css";

export const QRCodeAuth = ({
  userDataAccess,
  setCurrentRoute,
  registerdUserToken,
  setRegisterdUserToken,
  registeredUserEmail,
  setRegisteredUserEmail,
  setDeviceName,
  deviceName,
  isDeviceName,
  setIsDeviceName,
}) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [error, setError] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const refQRCode = useRef(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    usersPostRequest,
    setToken,
    setUserType,
    setOnBoardingRightsId,
    setRightsEnabled,
  } = useContextStateManagement();

  const handleKeyDown = (event) => {
    if (event.keyCode === 13 || event.which === 13) {
      handleVerify();
    }
  };

  const handleVerify = async () => {
    const obj = { registeredUserEmail, verificationCode };
    const userData = await usersPostRequest(
      registerdUserToken,
      "/api/verifyQRCode",
      obj,
    );

    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
      setQrCode("");
      setSecretKey("");
      return;
    }

    if (userData && userData.message === "success") {
      alert(
        "MFA setup complete. Two-factor authentication will now be required when signing in.",
      );

      if (userDataAccess && userDataAccess.length === 0) {
        setCurrentRoute("login");
        navigate("/login");
      } else {
        setRightsEnabled(false);
        setOnBoardingRightsId(1);
        dispatch(login({ ...userDataAccess, token: userData.token }));
        setCurrentRoute("");
        const redirectTo = sessionStorage.getItem("postLoginRedirect");
        sessionStorage.removeItem("postLoginRedirect");
        navigate(redirectTo || "/", { replace: true });
      }

      setError("");
      setIsDeviceName(false);
      setDeviceName("");
      setRegisterdUserToken("");
      setRegisteredUserEmail("");
      setQrCode("");
      setSecretKey("");
      return;
    }

    if (userData && userData === "Invalid token") {
      setError("Invalid code. Try again.");
      setIsDeviceName(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 30));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchQRCode = async () => {
    refQRCode.current = true;
    const userData = await usersPostRequest(
      registerdUserToken,
      "/api/generateQRCode",
      { registeredUserEmail, deviceName },
    );

    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true);
      setQrCode("");
      setSecretKey("");
      return;
    }

    if (userData && userData.qrCode) {
      setQrCode(userData.qrCode);
      setSecretKey(userData.secretKey);
      setTimeLeft(Math.trunc((userData.expiresAt - Date.now()) / 1000));
      return;
    }

    setIsDeviceName(false);
  };

  useEffect(() => {
    if (isDeviceName === true && refQRCode.current === false) {
      fetchQRCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeviceName]);

  // Token-expiry redirect is now handled once, centrally, in ContextApi.js - see its comment
  // for why this component's own copy of the same effect was removed.

  return (
    <div className="row login-start login-page-shell">
      <div className="col-12">
        <div className="login-layout login-layout--centered auth-layout--wide">
          <section className="login-form-panel auth-flip-card glass-panel setting-login auth-setup-panel">
            <div className="login-form-header">
              <p className="login-form-eyebrow">Authenticator App Setup</p>
              <p className="login-form-subtitle">
                Scan the QR code below or enter the secret key manually, then
                confirm the six-digit code from your authenticator app.
              </p>
            </div>

            <div className="auth-qr-layout">
              <div className="auth-qr-card">
                <span className="auth-section-card__eyebrow">QR Code</span>
                <div className="auth-qr-image-wrap">
                  {qrCode ? (
                    <img src={qrCode} alt="Authenticator QR code" className="auth-qr-image" />
                  ) : (
                    <div className="auth-qr-placeholder">Generating code...</div>
                  )}
                </div>
                <p className="auth-secondary-text">
                  Code refresh window: {timeLeft}s
                </p>
              </div>

              <div className="auth-qr-card auth-qr-card--copy">
                <span className="auth-section-card__eyebrow">Manual Entry</span>
                <h3>Secret Key</h3>
                <div className="auth-secret-key">{secretKey || "Waiting for secret key"}</div>
                <p className="auth-secondary-text">
                  Use this value only if your authenticator app cannot scan the QR
                  image.
                </p>

                <div className="auth-form-field auth-form-field--compact">
                  <label className="login-field-label" htmlFor="verificationCode">
                    Verification Code
                  </label>
                  <input
                    id="verificationCode"
                    type="text"
                    value={verificationCode}
                    onChange={(event) => {
                      setVerificationCode(event.target.value);
                      setError("");
                    }}
                    maxLength={6}
                    placeholder="000000"
                    onKeyDown={handleKeyDown}
                    className="login-field-input codeVerificationInput form-control text-center"
                  />
                  {error ? <small className="login-field-error">{error}</small> : null}
                </div>

                <div className="login-action-stack">
                  <button
                    type="button"
                    onClick={handleVerify}
                    className="simple-btn login-btn form-hidden login-submit-btn"
                    disabled={verificationCode.length !== 6}
                  >
                    Verify and complete setup
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
